import { AvailableModules } from 'generated/featureDefinitions'

import type { Option } from 'modules/core/components/Popover/Content'
import { Session } from 'modules/core/hooks/useGetCurrentSession'
import type { ModuleAvailabilityType } from 'modules/core/hooks/useModuleAvailability'
import { GraphQLClient } from 'modules/core/lib/graphql/provider'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'
import { track } from 'modules/core/lib/metrics'

// TODO: hardcoded URL. Country or locale is not considered.
const helpSupportUrl = 'https://help.factorialhr.com/how-can-i-contact-support'
const whatsAppUrl = 'https://wa.me/5511910867489'

const getCommonOptions = (
  graphQLClient: GraphQLClient | null,
  handleIntercomClick: () => void,
  isIntercomAvailable: boolean,
  session: Session,
  customCustomerSupportUrl?: string
): Option[] => {
  const {
    email,
    access: { firstName, userId },
    company,
  } = session
  const companyId = company.id
  const mainCompanyCountry = company.legalEntities.find((entity) => entity.main === true)?.country
  const showWhatsAppItem = mainCompanyCountry === 'br'

  const dropALineHref =
    customCustomerSupportUrl ||
    (showWhatsAppItem
      ? helpCenterUrl('new_request', { email, firstName, companyId })
      : 'https://portal.support.factorialhr.com/servicedesk/customer/portal/5')

  const commonOptions: Option[] = [
    {
      title: i18n.t('sidebar.add_button.actions.contact_support.title'),
      description: `${i18n.t(
        'sidebar.add_button.actions.contact_support.subtitle'
      )} User ID: #${userId}`,
      href: helpSupportUrl,
      onClick: () => track(graphQLClient, 'Clicked contact support'),
      icon: 'phone',
      target: '_blank',
    },
  ]

  if (isIntercomAvailable) {
    const intercomChatOption: Option = {
      title: i18n.t('sidebar.add_button.actions.chat_with_us.title'),
      description: i18n.t('sidebar.add_button.actions.chat_with_us.subtitle'),
      href: undefined,
      onClick: () => handleIntercomClick(),
      icon: 'comment',
      target: undefined,
    }
    commonOptions.push(intercomChatOption)
  } else if (showWhatsAppItem) {
    const whatsAppLinkOption: Option = {
      title: i18n.t('sidebar.add_button.actions.chat_with_us.title'),
      description: i18n.t('sidebar.add_button.actions.chat_with_us.subtitle_2'),
      href: whatsAppUrl,
      icon: 'comment',
      target: '_blank',
    }
    commonOptions.push(whatsAppLinkOption)
  }

  return commonOptions
}

function getFeedbackOptions(): Option[] {
  return [
    {
      title: i18n.t('sidebar.add_button.actions.help_center.title'),
      href: helpCenterUrl(),
      target: '_blank',
    },
  ]
}

function getDomainOptions(
  graphQLClient: GraphQLClient | null,
  moduleAvailabilities: ModuleAvailabilityType[],
  isIntercomAvailable: boolean,
  session: Session,
  customCustomerSupportUrl?: string
): Option[] {
  if (!session.company) return []

  const {
    email,
    access: { firstName },
    company,
  } = session
  const { countryFactorialDomain: domain, slug, id: companyId } = company
  const canSeeComplaints =
    isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.complaints) && slug && domain
  const options: Option[] = []

  const mainCompanyCountry = company.legalEntities.find((entity) => entity.main === true)?.country
  const showBrazilianSupport = mainCompanyCountry === 'br'
  const dropALineHref =
    customCustomerSupportUrl ||
    (showBrazilianSupport
      ? helpCenterUrl('new_request', { email, firstName, companyId })
      : 'https://portal.support.factorialhr.com/servicedesk/customer/portal/5')

  options.push({
    title: i18n.t('sidebar.add_button.actions.submit_ticket.title'),
    href: dropALineHref,
    onClick: () => track(graphQLClient, 'Clicked submit ticket'),
    target: '_blank',
  })

  if (canSeeComplaints) {
    options.push({
      title: i18n.t('sidebar.add_button.actions.submit_complaint.title'),
      href: `http://${slug}.${domain}/complaints`,
      target: '_blank',
      onClick: () => track(graphQLClient, 'Clicked complaint link'),
    })
  }

  return options
}

function getSeparatorOption(): Option {
  return {}
}

// TODO: Migrate this to PopoverMenu when it's ready!
export function getOptions(
  graphQLClient: GraphQLClient | null,
  moduleAvailabilities: ModuleAvailabilityType[],
  handleIntercomClick: () => void,
  showIntercomChat: boolean,
  session: Session,
  customCustomerSupportUrl?: string
): Option[] {
  const { isIntercomAllowed } = session
  const isIntercomAvailable = isIntercomAllowed && showIntercomChat
  const options: Option[] = getCommonOptions(
    graphQLClient,
    handleIntercomClick,
    isIntercomAvailable,
    session,
    customCustomerSupportUrl
  )
  const domainOptions = getDomainOptions(
    graphQLClient,
    moduleAvailabilities,
    isIntercomAvailable,
    session,
    customCustomerSupportUrl
  )
  const feedbackOptions = getFeedbackOptions()

  if (domainOptions.length) {
    options.push(getSeparatorOption())
    options.push(...domainOptions)
  }

  if (feedbackOptions.length) {
    options.push(getSeparatorOption())
    options.push(...feedbackOptions)
  }

  return options
}
